<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <b-row class="align-items-center">
          <b-col
            md="5"
            class="mb-1 mb-md-0"
          >
            <v-select
              v-model="selectedHosts"
              :options="hosts"
              multiple
              placeholder="Все ведущие"
              :close-on-select="false"
            />
          </b-col>
          <b-col
            md="4"
            class="mb-1 mb-md-0"
          >
            <b-form-checkbox-group
              v-model="selectedTypes"
              :options="typeOptions"
            />
          </b-col>
          <b-col
            md="3"
            class="d-flex align-items-center justify-content-md-end"
          >
            <b-spinner
              v-if="isLoading"
              small
              class="mr-1"
            />
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="isLoading"
              @click="refresh"
            >
              <feather-icon
                icon="RefreshCwIcon"
                class="mr-50"
              />
              Обновить из Planfix
            </b-button>
          </b-col>
        </b-row>

        <div
          v-if="hosts.length"
          class="d-flex flex-wrap mt-1"
        >
          <span
            v-for="host in hosts"
            :key="host"
            class="host-chip mr-50 mb-50"
            :class="{ 'host-chip-inactive': selectedHosts.length && !selectedHosts.includes(host) }"
            :style="{ '--host-color': hostColor(host), '--host-tint': hostTint(host) }"
            title="Показать только этого ведущего / снять"
            @click="toggleHost(host)"
          >{{ host }}</span>
        </div>

        <div class="d-flex flex-wrap align-items-center mt-50 small text-muted">
          <span class="mr-2"><feather-icon icon="AwardIcon" /> Игра Лиги</span>
          <span class="mr-2"><feather-icon icon="LockIcon" /> Закрытая игра</span>
          <span class="mr-2"><span class="legend-private" /> Частная</span>
          <span class="mr-2">
            Игроки:
            <span class="badge badge-secondary">0–1</span>
            <span class="badge badge-warning">2</span>
            <span class="badge badge-success">3+</span>
          </span>
          <span>Прошедшие игры — из синка, будущие — из Planfix в реальном времени</span>
        </div>

        <b-alert
          :show="liveError"
          variant="warning"
          class="mt-1 mb-0"
        >
          <div class="alert-body">
            Planfix не ответил — будущие игры показаны из базы и могут быть неактуальны.
          </div>
        </b-alert>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-card-body>
        <full-calendar
          ref="refCalendar"
          :options="calendarOptions"
          class="full-calendar games-calendar"
        />
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BButton, BSpinner, BAlert, BFormCheckboxGroup,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import ruLocale from '@fullcalendar/core/locales/ru'
import { ref, watch, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import gameStoreModule from '@/views/game/gameStoreModule'

const STORE_MODULE_NAME = 'app-games'
const HOST_COLORS_STORAGE_KEY = 'games-calendar:host-colors'

const HOST_PALETTE = [
  '#7367f0', '#28c76f', '#ea5455', '#ff9f43', '#00cfe8', '#e83e8c',
  '#20c997', '#6f42c1', '#fd7e14', '#3b82f6', '#a16207', '#64748b',
]
const NO_HOST_COLOR = '#b9b9c3'

const TYPE_LABELS = {
  league: 'Лига',
  closed: 'Закрытая',
}

// Иконки feather (award / lock) строкой — eventContent отдаёт голый HTML, Vue-компоненты туда не смонтировать
const TYPE_ICONS = {
  league: '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></svg>',
  closed: '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>',
}

const STATUS_LABELS = {
  planned: 'запланирована',
  held: 'проведена',
  no_show: 'не состоялась',
}

const escapeHtml = value => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')

const playersBadgeVariant = count => {
  if (count >= 3) return 'success'
  if (count === 2) return 'warning'
  return 'secondary'
}

const hexToRgba = (hex, alpha) => {
  const value = parseInt(hex.slice(1), 16)
  // eslint-disable-next-line no-bitwise
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`
}

/**
 * Закрепление цвета за ведущим: первый увиденный ведущий получает первый свободный цвет палитры,
 * соответствие хранится в localStorage, чтобы цвета не прыгали при листании месяцев.
 */
const loadHostColorIndexes = () => {
  try {
    return JSON.parse(localStorage.getItem(HOST_COLORS_STORAGE_KEY)) || {}
  } catch (e) {
    return {}
  }
}

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BAlert,
    BFormCheckboxGroup,
    vSelect,
    FullCalendar,
  },
  setup() {
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, gameStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const toast = useToast()
    const refCalendar = ref(null)
    const hosts = ref([])
    const selectedHosts = ref([])
    const selectedTypes = ref(['league', 'closed'])
    const typeOptions = [
      { text: 'Игры Лиги', value: 'league' },
      { text: 'Закрытые игры', value: 'closed' },
    ]
    const isLoading = ref(false)
    const liveError = ref(false)
    const hostColorIndexes = ref(loadHostColorIndexes())
    let refreshNext = false

    const assignHostColors = hostNames => {
      const indexes = { ...hostColorIndexes.value }
      let changed = false
      hostNames.forEach(host => {
        if (indexes[host] !== undefined) return
        const used = new Set(Object.values(indexes))
        const free = HOST_PALETTE.findIndex((color, index) => !used.has(index))
        indexes[host] = free >= 0 ? free : Object.keys(indexes).length % HOST_PALETTE.length
        changed = true
      })
      if (!changed) return
      hostColorIndexes.value = indexes
      try {
        localStorage.setItem(HOST_COLORS_STORAGE_KEY, JSON.stringify(indexes))
      } catch (e) {
        // приватный режим / запрет хранилища — цвета просто не запомнятся между визитами
      }
    }

    const hostColor = host => {
      const index = host ? hostColorIndexes.value[host] : undefined
      return index !== undefined ? HOST_PALETTE[index] : NO_HOST_COLOR
    }
    const hostTint = host => hexToRgba(hostColor(host), 0.16)

    const toggleHost = host => {
      selectedHosts.value = selectedHosts.value.includes(host)
        ? selectedHosts.value.filter(selected => selected !== host)
        : [...selectedHosts.value, host]
    }

    const toCalendarEvent = game => ({
      id: game.id,
      title: game.title,
      start: game.start,
      end: game.end || undefined,
      allDay: !game.has_time,
      classNames: [
        game.is_private ? 'game-private' : null,
        game.status === 'held' ? 'game-held' : null,
        game.status === 'no_show' ? 'game-no-show' : null,
      ].filter(Boolean),
      extendedProps: game,
    })

    const fetchEvents = (info, successCallback, failureCallback) => {
      // Ни одного типа не выбрано — показывать нечего, в API не ходим
      if (selectedTypes.value.length === 0) {
        successCallback([])
        return
      }
      isLoading.value = true
      const params = {
        from: info.startStr,
        to: info.endStr,
        hosts: selectedHosts.value,
        types: selectedTypes.value,
        refresh: refreshNext ? 1 : undefined,
      }
      refreshNext = false
      store.dispatch(`${STORE_MODULE_NAME}/fetchGameCalendar`, params)
        .then(({ data }) => {
          hosts.value = Array.from(new Set([...data.hosts, ...selectedHosts.value])).sort()
          assignHostColors(hosts.value)
          liveError.value = data.live_error
          successCallback(data.events.map(toCalendarEvent))
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Не удалось загрузить календарь игр',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          failureCallback(error)
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    const refetch = () => refCalendar.value?.getApi().refetchEvents()

    const refresh = () => {
      refreshNext = true
      refetch()
    }

    watch([selectedHosts, selectedTypes], refetch)

    const describeEvent = game => [
      `${TYPE_LABELS[game.type]}: ${game.title}`,
      game.pack_name && game.pack_name !== game.title ? `Пакет: ${game.pack_name}` : null,
      `Ведущий: ${game.host_name || '—'}`,
      `Игроков: ${game.players_count}`,
      game.is_private ? 'Частная игра' : null,
      STATUS_LABELS[game.status] ? `Статус: ${STATUS_LABELS[game.status]}` : null,
    ].filter(Boolean).join('\n')

    const renderEventContent = arg => {
      const game = arg.event.extendedProps
      const isList = arg.view.type.startsWith('list')
      // В сетке месяца места мало — ведущий виден по цвету, имя только в подсказке
      const showHost = game.host_name && arg.view.type !== 'dayGridMonth'
      const badge = `<span class="badge badge-pill badge-${playersBadgeVariant(game.players_count)} game-event-players">${game.players_count}</span>`
      const title = `${escapeHtml(arg.event.title)}${showHost ? ` <span class="game-event-host">· ${escapeHtml(game.host_name)}</span>` : ''}`

      // В неделе/дне параллельные игры делят колонку: время и игроки строкой сверху,
      // название под ними и обрезается по высоте блока, полный текст — в подсказке
      if (arg.view.type.startsWith('timeGrid') && !arg.event.allDay) {
        return {
          html: `<div class="game-event game-event-timegrid">
            <div class="game-event-head">
              <span class="game-event-icon">${TYPE_ICONS[game.type] || ''}</span>
              <span class="game-event-time">${escapeHtml(arg.timeText)}</span>
              ${badge}
            </div>
            <div class="game-event-title">${title}</div>
          </div>`,
        }
      }

      return {
        html: `<div class="game-event">
          <span class="game-event-icon">${TYPE_ICONS[game.type] || ''}</span>
          ${!isList && arg.timeText ? `<span class="game-event-time">${escapeHtml(arg.timeText)}</span>` : ''}
          <span class="game-event-title">${title}</span>
          ${badge}
        </div>`,
      }
    }

    const calendarOptions = {
      plugins: [dayGridPlugin, timeGridPlugin, listPlugin],
      locale: ruLocale,
      firstDay: 1,
      initialView: 'dayGridMonth',
      headerToolbar: {
        start: 'prev,next today title',
        end: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth',
      },
      height: 'auto',
      dayMaxEvents: 4,
      // Одновременные игры — рядом в колонках, а не внахлёст
      slotEventOverlap: false,
      // Длительность по умолчанию — для событий без конца (бэкенд обычно присылает end)
      defaultTimedEventDuration: '01:30',
      // Метка времени у блока только начальная: «10:30», а не «10:30 - 12:00»
      displayEventEnd: false,
      eventDisplay: 'block',
      eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
      slotLabelFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
      events: fetchEvents,
      eventContent: renderEventContent,
      eventDidMount: info => {
        const { host_name: hostName } = info.event.extendedProps
        info.el.style.setProperty('--host-color', hostColor(hostName))
        info.el.style.setProperty('--host-tint', hostTint(hostName))
        info.el.setAttribute('title', describeEvent(info.event.extendedProps))
      },
      eventClick: info => {
        info.jsEvent.preventDefault()
        const url = info.event.extendedProps.task_url
        if (url) window.open(url, '_blank', 'noopener')
      },
    }

    return {
      refCalendar,
      calendarOptions,
      hosts,
      selectedHosts,
      selectedTypes,
      typeOptions,
      isLoading,
      liveError,
      refresh,
      hostColor,
      hostTint,
      toggleHost,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/apps/calendar.scss';
@import '@core/scss/vue/libs/vue-select.scss';

.games-calendar {
  .fc-event {
    cursor: pointer;
  }

  // Цвет ведущего приходит CSS-переменными из eventDidMount
  .fc-daygrid-event,
  .fc-timegrid-event {
    background-color: var(--host-tint);
    border: 1px solid var(--host-color);
    border-left-width: 4px;
    white-space: normal;

    .fc-event-main {
      color: inherit;
    }
  }

  .fc-list-event-dot {
    border-color: var(--host-color);
  }

  .game-event {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    padding: 1px 2px;
    font-size: 0.85rem;
    line-height: 1.25;
    overflow: hidden;
  }

  .game-event-icon {
    flex: 0 0 auto;
    color: var(--host-color);
    line-height: 1;
    padding-top: 2px;
  }

  .game-event-time {
    flex: 0 0 auto;
    font-weight: 600;
  }

  .game-event-title {
    flex: 1 1 auto;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .game-event-host {
    opacity: 0.75;
  }

  .game-event-players {
    flex: 0 0 auto;
    font-size: 0.7rem;
    margin-left: auto;
  }

  // Повыше слоты, чтобы в 90-минутную игру влезало название
  .fc-timegrid-slot {
    height: 2em;
  }

  .fc-timegrid-event .fc-event-main {
    overflow: hidden;
  }

  .game-event-timegrid {
    flex-direction: column;
    gap: 1px;
    height: 100%;

    .game-event-head {
      display: flex;
      align-items: center;
      gap: 4px;
      width: 100%;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
    }

    .game-event-title {
      flex: 1 1 auto;
      width: 100%;
      overflow: hidden;
      overflow-wrap: break-word;
      font-size: 0.8rem;
    }
  }

  .game-private.fc-daygrid-event,
  .game-private.fc-timegrid-event {
    border-style: dashed;
  }

  .game-held {
    opacity: 0.7;
  }

  .game-no-show .game-event-title {
    text-decoration: line-through;
  }
}

.host-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  border: 1px solid var(--host-color);
  background-color: var(--host-tint);
  font-size: 0.85rem;
  cursor: pointer;
  user-select: none;

  &.host-chip-inactive {
    opacity: 0.4;
  }
}

.legend-private {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  border: 1px dashed currentColor;
  vertical-align: middle;
}
</style>
